import { createApp,markRaw } from "vue";
import { createPinia } from "pinia";
import { plugin, defaultConfig,createInput  } from '@formkit/vue'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import VCalendar from 'v-calendar';
import 'v-calendar/style.css';
import { de } from '@formkit/i18n'
import { genesisIcons } from '@formkit/icons'
import { createProPlugin, inputs } from '@formkit/pro'
import '@formkit/themes/genesis'
import '@formkit/pro/genesis'
import App from "./App.vue";
import router from "./router";
import toast, { POSITION, useToast } from "vue-toastification";
import "vue-toastification/dist/index.css";
import useNotification from "./composables/useNotification.js";


import "./assets/base.css";

import FormkitWysiwig from './helper/FormkitWysiwig.vue'
import FormkitDatabaseDropdown from './helper/FormkitDatabaseDropdown.vue'
import i18n from './helper/i18n.js';


import  "./assets/brand/base.css";
import  "./assets/brand/typography.css";
import  "./assets/brand/buttons.css";
import  "./assets/brand/kickers.css";
import  "./assets/brand/checkboxes.css";
import  "./assets/brand/loginForm.css";
import  "./assets/brand/dividers.css";
import  "./assets/brand/nuggets.css";
import  "./assets/brand/buttonGroup.css";
import  "./assets/brand/navigation.css";
import  "./assets/brand/inputs.css";
import  "./assets/brand/selects.css";

const pro = createProPlugin('fk-63ac44e5b', inputs)

const app = createApp(App);
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate)



app.use(
    plugin,
    defaultConfig({
        plugins: [pro],
        theme: 'genesis',
        icons: {
          ...genesisIcons
        },
        inputs: {
            wys: createInput(FormkitWysiwig),
            dbdropdown: createInput(FormkitDatabaseDropdown, {
              props: ['filter','collection','fieldname','placeholder','limit','fields','sort','link_url','link_text'],
            }),
        },
        locales: { de },
        locale: 'en-EN'
    })
);
app.use(toast,{
  position: POSITION.BOTTOM_RIGHT
});
app.use(i18n);
app.use(pinia);
app.use(router);
app.use(VCalendar, {})

// Refresh unread notifications on every navigation. The composable also binds
// a tab-focus listener once MainNav mounts; there's no polling interval, so
// idle tabs are quiet.
router.afterEach(() => {
  useNotification().getNotifications();
});
pinia.use(({ store }) => {
    store.router = markRaw(router)
})


import * as Sentry from "@sentry/vue";

Sentry.init({
  app: app,
  dsn: "https://6485b33810cea0df5aa5fd558a1c68af@o4504530459951104.ingest.sentry.io/4506739739852800",
  integrations: [
    Sentry.browserTracingIntegration(),
  ],
  tracesSampleRate: 1.0, // Capture 100% of transactions for performance monitoring
  tracePropagationTargets: ["localhost", /^https:\/\/cstintranet\.chemspeed.com/],
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0.0,
  ignoreErrors: [
    // FormKit "aborted" — happens when user navigates away during form submission
    /^aborted$/,
    // Network errors — transient connectivity issues, not actionable
    'Failed to fetch',
    'NetworkError when attempting to fetch resource',
    'Network request failed',
    // WebSocket re-auth — handled by fallback to polling
    'No token for re-authenticating the websocket',
  ],
});

app.mount("#app");

// Register service worker for asset caching and push notifications.
// Detects when a new SW version has installed and is waiting to take over,
// then surfaces a one-click "Reload to update" toast so users don't need to
// hit Ctrl+F5 to pick up new builds. The SW itself uses network-first for
// HTML so a plain F5 also picks up new asset URLs immediately.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw-push.js', { scope: '/' })
    .then((registration) => {
      // Periodic + on-focus update checks so long-lived tabs see new builds.
      const triggerUpdate = () => registration.update().catch(() => {});
      setInterval(triggerUpdate, 30 * 60 * 1000);
      window.addEventListener('focus', triggerUpdate);

      const promptToReload = (worker) => {
        const $toast = useToast();
        $toast.info('A new version of Orbit is available — click to reload.', {
          timeout: false,
          closeOnClick: true,
          onClick: () => {
            worker.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      };

      // SW already waiting at registration time → surface prompt immediately.
      if (registration.waiting && navigator.serviceWorker.controller) {
        promptToReload(registration.waiting);
      }

      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            promptToReload(newWorker);
          }
        });
      });

      // After the new SW takes control (skipWaiting → controllerchange),
      // reload once so the freshly fetched HTML + assets are running. Skipped
      // on the very first install (no prior controller) to avoid a startup
      // flash for first-time visitors.
      let refreshing = false;
      const wasControlled = !!navigator.serviceWorker.controller;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing || !wasControlled) return;
        refreshing = true;
        window.location.reload();
      });
    })
    .catch(() => {
      // Service worker registration failed — caching won't work but app is fine
    });
}